import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { AccountModel } from '../../../../../../domain/models/account.model';
import {
  AccountBankAccountDto,
  BankAccountPurpose,
  BankAccountStatus,
  BankAccountType
} from '../../../../../../domain/models/bank-account.model';
import { AccountBankingService } from '../../../../../../domain/services/account-banking/account-banking.service';
import { DialogService } from '../../../../../../domain/services/dialog/dialog.service';

/**
 * Account-scoped BANKING tab: list (masked), add/edit/remove, set default.
 * Mirrors the masked-wallet pattern; the full account number is never displayed.
 */
@Component({
  selector: 'app-account-banking-tab',
  standalone: true,
  imports: [
    CommonModule, FormsModule, MatTableModule, MatButtonModule, MatIconModule,
    MatFormFieldModule, MatInputModule, MatSelectModule, MatProgressSpinnerModule, MatChipsModule
  ],
  templateUrl: './account-banking-tab.component.html',
  styleUrl: './account-banking-tab.component.css'
})
export class AccountBankingTabComponent implements OnInit {
  @Input() account!: AccountModel;

  bankAccounts: AccountBankAccountDto[] = [];
  displayedColumns = ['bankName', 'accountNumberLast4', 'accountType', 'purpose', 'status', 'default', 'actions'];
  isLoading = true;
  loadError: string | null = null;
  saveError: string | null = null;

  showForm = false;
  editingId: number | null = null;

  accountTypes: BankAccountType[] = ['CHECKING', 'SAVINGS'];
  purposes: BankAccountPurpose[] = ['DEBIT', 'REFUND', 'BOTH'];
  statuses: BankAccountStatus[] = ['ACTIVE', 'INACTIVE'];

  form = this.emptyForm();

  constructor(private bankingService: AccountBankingService, private dialogService: DialogService) {}

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    if (!this.account?.id) {
      this.isLoading = false;
      return;
    }
    this.isLoading = true;
    this.loadError = null;
    this.bankingService.list(this.account.id).subscribe({
      next: (rows) => { this.bankAccounts = rows; this.isLoading = false; },
      error: (err) => { console.error('Error loading bank accounts:', err); this.loadError = 'Unable to load bank accounts.'; this.isLoading = false; }
    });
  }

  startAdd(): void {
    this.editingId = null;
    this.form = this.emptyForm();
    this.saveError = null;
    this.showForm = true;
  }

  startEdit(b: AccountBankAccountDto): void {
    this.editingId = b.id;
    this.form = {
      accountHolderName: b.accountHolderName ?? '',
      bankName: b.bankName ?? '',
      routingNumber: b.routingNumber,
      accountNumber: '',
      accountType: b.accountType,
      purpose: b.purpose,
      status: b.status
    };
    this.saveError = null;
    this.showForm = true;
  }

  cancel(): void {
    this.showForm = false;
    this.editingId = null;
    this.form = this.emptyForm();
    this.saveError = null;
  }

  save(): void {
    if (!this.account?.id) { return; }
    this.saveError = null;
    if (this.editingId !== null) {
      this.bankingService.update(this.account.id, this.editingId, {
        accountHolderName: this.form.accountHolderName,
        bankName: this.form.bankName,
        accountType: this.form.accountType,
        purpose: this.form.purpose,
        status: this.form.status
      }).subscribe({
        next: () => { this.cancel(); this.load(); },
        error: (err) => this.onSaveError(err)
      });
    } else {
      this.bankingService.create(this.account.id, {
        accountHolderName: this.form.accountHolderName,
        bankName: this.form.bankName,
        routingNumber: this.form.routingNumber,
        accountNumber: this.form.accountNumber,
        accountType: this.form.accountType,
        purpose: this.form.purpose
      }).subscribe({
        next: () => { this.cancel(); this.load(); },
        error: (err) => this.onSaveError(err)
      });
    }
  }

  setDefault(b: AccountBankAccountDto): void {
    if (!this.account?.id || b.isDefault) { return; }
    this.bankingService.setDefault(this.account.id, b.id).subscribe({
      next: () => this.load(),
      error: (err) => console.error('Error setting default:', err)
    });
  }

  remove(b: AccountBankAccountDto): void {
    if (!this.account?.id) { return; }
    this.dialogService.openDeletedDialog(
      'Remove Bank Account',
      'Are you sure you want to remove this bank account? This action cannot be undone.',
      (confirmed: boolean) => {
        if (confirmed) {
          this.bankingService.remove(this.account.id!, b.id).subscribe({
            next: () => this.load(),
            error: (err) => console.error('Error removing bank account:', err)
          });
        }
      }
    );
  }

  private onSaveError(err: any): void {
    console.error('Error saving bank account:', err);
    this.saveError = err?.error?.message || 'Unable to save bank account. Check the routing/account numbers.';
  }

  private emptyForm() {
    return {
      accountHolderName: '',
      bankName: '',
      routingNumber: '',
      accountNumber: '',
      accountType: 'CHECKING' as BankAccountType,
      purpose: 'REFUND' as BankAccountPurpose,
      status: 'ACTIVE' as BankAccountStatus
    };
  }
}
